import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { OpeningSideHolePlacement } from "building-types/openings/opening";
import { type OpeningSide, type OpeningSideContext } from "building-types/openings/openingTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import { defaultExteriorDoorCasingSettings } from "doc/settings/defaultSettings";
import { planeCopy, planeNormal } from "geom/plane";
import { polyLine3OnPlane } from "geom/polyLine3";
import { makeDoorSide } from "../doors/doorElevation";
import { doorCasingClipsFinish, doorHasJamb, doorHasWrapping, placementIsExterior } from "../doors/doorQuery";
import { reprojectProfiles } from "../walls/openingProfiles";
import { planExtentsFromElevations } from "./openingUtils";
import { wallOpeningNew } from "./wallOpeningNew";

export function doorSide(
  context: OpeningSideContext,
  door: GeneratedDoorPlacement,
  casingSettings: InteriorDoorCasingSettings | ExteriorDoorCasingSettings,
  placement: OpeningSideHolePlacement,
  profileRectangular: OpeningSide,
  mirror: boolean,
): OpeningSide {
  const openingPlane = planeCopy(placement.openingPlane);

  let projected = reprojectProfiles(profileRectangular, placement.edgePlane, openingPlane);

  const profiles = makeDoorSide(
    {
      ...context,
      openingPlane,
    },
    door,
    casingSettings,
    projected,
    openingPlane,
    mirror,
  );

  // save the casing profiles for the opening
  projected = {
    ...projected,
    ...profiles,
  };

  // reproject the new casing profiles
  projected.opening3 = polyLine3OnPlane(projected.opening2, openingPlane);
  projected.openingCasing3 = polyLine3OnPlane(projected.openingCasing2, openingPlane);

  return projected;
}

export function doorOpeningNew(
  door: GeneratedDoorPlacement,
  frontPlacement: OpeningSideHolePlacement,
  frontProfileRectangular: OpeningSide,
  backPlacement: OpeningSideHolePlacement,
  backProfileRectangular: OpeningSide,
  mirror: boolean,
): WallOpening {
  const frontPlane = planeCopy(frontPlacement.openingPlane);
  const backPlane = planeCopy(backPlacement.openingPlane);

  const frontIsExterior = placementIsExterior(frontPlacement);
  const frontProfile = doorSide(
    {
      condition: frontIsExterior ? "EXTERIOR" : "INTERIOR",
    },
    door,
    frontIsExterior
      ? (door.exteriorCasing as ExteriorDoorCasingSettings) || defaultExteriorDoorCasingSettings
      : (door.interiorCasing as InteriorDoorCasingSettings),
    frontPlacement,
    frontProfileRectangular,
    mirror,
  );

  const backIsExterior = placementIsExterior(backPlacement);
  const backProfile = doorSide(
    {
      condition: backIsExterior ? "EXTERIOR" : "INTERIOR",
    },
    door,
    backIsExterior
      ? (door.exteriorCasing as ExteriorDoorCasingSettings) || defaultExteriorDoorCasingSettings
      : (door.interiorCasing as InteriorDoorCasingSettings),
    backPlacement,
    backProfileRectangular,
    mirror,
  );

  const [px, py] = planeNormal(frontPlane);

  const planExtents = planExtentsFromElevations([px, py], frontProfile.opening3, backProfile.opening3);
  const planExtentsCasing = planExtentsFromElevations(
    [px, py],
    frontProfile.openingCasing3,
    backProfile.openingCasing3,
  );

  return wallOpeningNew({
    handle: { entityType: "DOOR", id: door.id },
    hasExteriorCasing: door.hasExteriorCasing,
    casingClipsFinish: doorCasingClipsFinish(door, door.exteriorCasing as ExteriorDoorCasingSettings),
    hasJamb: doorHasJamb(door),
    hasWrapping: doorHasWrapping(door),
    hasSill: true,
    front: frontProfile,
    back: backProfile,
    frontPlane,
    backPlane,
    frontRoom: frontPlacement.room,
    backRoom: backPlacement.room,
    planExtents,
    planExtentsCasing,
    mirror,
  });
}
